/**
 * 🐾 Sovereign Dual-Layer Secure Storage Engine for Fissal Mobile
 * 
 * Hardware-backed session persistence:
 * 1. Synchronous In-Memory & LocalStorage cache for instantaneous React initializations.
 * 2. Asynchronous Native SharedPreferences (@capacitor/preferences) for unshakeable
 *    survival across Android APK updates, WebView process restarts, and network roaming.
 */

import { Preferences } from '@capacitor/preferences';
import { AuthUser, UserProfile } from '../types';

export const STORAGE_KEY_AUTH_TOKEN = 'fissal_auth_token';
export const STORAGE_KEY_AUTH_USER = 'fissal_auth_user';
export const STORAGE_KEY_PROFILE = 'fissal_profile';
export const STORAGE_KEY_SERVER_URL = 'fissal_server_url';
export const STORAGE_KEY_NETWORK_PRESET = 'fissal_network_preset';

// In-Memory Fast Cache
let memoryToken: string | null = null;
let memoryUser: AuthUser | null = null;
let memoryProfile: UserProfile | null = null;
let isHydrated = false;

export function normalizeUser(u: any): AuthUser | null {
  if (!u) return null;
  const name = u.displayName || u.display_name || (u.userId === 'echo' ? 'Echo' : u.userId === 'mom' ? 'Sharon (Mom)' : u.userId || 'User');
  return {
    ...u,
    displayName: name,
    display_name: name
  };
}

// Synchronous initialization from localStorage if available
try {
  if (typeof window !== 'undefined' && window.localStorage) {
    memoryToken = localStorage.getItem(STORAGE_KEY_AUTH_TOKEN);
    const rawUser = localStorage.getItem(STORAGE_KEY_AUTH_USER);
    if (rawUser) {
      try {
        memoryUser = normalizeUser(JSON.parse(rawUser));
      } catch {}
    }
    const rawProfile = localStorage.getItem(STORAGE_KEY_PROFILE);
    if (rawProfile) {
      memoryProfile = rawProfile as UserProfile;
    }
  }
} catch (err) {
  console.warn('[SecureStorage] LocalStorage read warning:', err);
}

/**
 * Hydrates secure session from native Android SharedPreferences.
 * If WebView LocalStorage was evicted by an update or process recreation,
 * this restores the credentials from native storage.
 */
export async function hydrateSecureSession(): Promise<{
  token: string | null;
  user: AuthUser | null;
  profile: UserProfile | null;
}> {
  try {
    const [tokenRes, userRes, profileRes] = await Promise.all([
      Preferences.get({ key: STORAGE_KEY_AUTH_TOKEN }),
      Preferences.get({ key: STORAGE_KEY_AUTH_USER }),
      Preferences.get({ key: STORAGE_KEY_PROFILE })
    ]);

    const nativeToken = tokenRes.value;
    let nativeUser: AuthUser | null = null;
    if (userRes.value) {
      try {
        nativeUser = normalizeUser(JSON.parse(userRes.value));
      } catch {}
    }
    const nativeProfile = userRes.value ? (profileRes.value as UserProfile) : null;

    // Case 1: Native storage has credentials, but localStorage/memory was empty (e.g. after APK update)
    if (nativeToken && !memoryToken) {
      console.log('🐾 [SecureStorage] Restoring session from Native Android SharedPreferences!');
      memoryToken = nativeToken;
      memoryUser = nativeUser;
      if (nativeProfile) memoryProfile = nativeProfile;

      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(STORAGE_KEY_AUTH_TOKEN, nativeToken);
        if (nativeUser) localStorage.setItem(STORAGE_KEY_AUTH_USER, JSON.stringify(nativeUser));
        if (nativeProfile) localStorage.setItem(STORAGE_KEY_PROFILE, nativeProfile);
      }
    } 
    // Case 2: LocalStorage had credentials, but Native storage didn't yet (initial sync)
    else if (memoryToken && !nativeToken) {
      console.log('🐾 [SecureStorage] Synchronizing credentials into Native Android SharedPreferences...');
      await Preferences.set({ key: STORAGE_KEY_AUTH_TOKEN, value: memoryToken });
      if (memoryUser) {
        await Preferences.set({ key: STORAGE_KEY_AUTH_USER, value: JSON.stringify(memoryUser) });
      }
      if (memoryProfile) {
        await Preferences.set({ key: STORAGE_KEY_PROFILE, value: memoryProfile });
      }
    }

    isHydrated = true;
  } catch (err) {
    console.warn('[SecureStorage] Native hydration warning:', err);
  }

  return {
    token: memoryToken,
    user: memoryUser,
    profile: memoryProfile
  };
}

export function isSessionHydrated(): boolean {
  return isHydrated;
}

// Synchronous fast getters
export function getStoredToken(): string | null {
  if (memoryToken) return memoryToken;
  try {
    return localStorage.getItem(STORAGE_KEY_AUTH_TOKEN);
  } catch {
    return null;
  }
}

export function getStoredAuthUser(): AuthUser | null {
  if (memoryUser) return memoryUser;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_AUTH_USER);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getStoredProfile(): UserProfile | null {
  if (memoryProfile) return memoryProfile;
  try {
    return (localStorage.getItem(STORAGE_KEY_PROFILE) as UserProfile) || null;
  } catch {
    return null;
  }
}

// Dual-layer setters (writes to Memory, LocalStorage, and Native SharedPreferences)
export function setStoredToken(token: string | null) {
  memoryToken = token;
  try {
    if (token) {
      localStorage.setItem(STORAGE_KEY_AUTH_TOKEN, token);
    } else {
      localStorage.removeItem(STORAGE_KEY_AUTH_TOKEN);
    }
  } catch {}

  // Background sync to native storage
  if (token) {
    Preferences.set({ key: STORAGE_KEY_AUTH_TOKEN, value: token }).catch(err =>
      console.warn('[SecureStorage] Preferences.set token failed:', err)
    );
  } else {
    Preferences.remove({ key: STORAGE_KEY_AUTH_TOKEN }).catch(err =>
      console.warn('[SecureStorage] Preferences.remove token failed:', err)
    );
  }
}

export function setStoredAuthUser(user: AuthUser | null) {
  const normUser = normalizeUser(user);
  memoryUser = normUser;
  try {
    if (normUser) {
      localStorage.setItem(STORAGE_KEY_AUTH_USER, JSON.stringify(normUser));
    } else {
      localStorage.removeItem(STORAGE_KEY_AUTH_USER);
    }
  } catch {}

  if (normUser) {
    Preferences.set({ key: STORAGE_KEY_AUTH_USER, value: JSON.stringify(normUser) }).catch(err =>
      console.warn('[SecureStorage] Preferences.set user failed:', err)
    );
  } else {
    Preferences.remove({ key: STORAGE_KEY_AUTH_USER }).catch(err =>
      console.warn('[SecureStorage] Preferences.remove user failed:', err)
    );
  }
}

export function setStoredProfile(profile: UserProfile | null) {
  memoryProfile = profile;
  try {
    if (profile) {
      localStorage.setItem(STORAGE_KEY_PROFILE, profile);
    } else {
      localStorage.removeItem(STORAGE_KEY_PROFILE);
    }
  } catch {}

  if (profile) {
    Preferences.set({ key: STORAGE_KEY_PROFILE, value: profile }).catch(err =>
      console.warn('[SecureStorage] Preferences.set profile failed:', err)
    );
  } else {
    Preferences.remove({ key: STORAGE_KEY_PROFILE }).catch(err =>
      console.warn('[SecureStorage] Preferences.remove profile failed:', err)
    );
  }
}

/**
 * Complete teardown of all stored credentials (used ONLY on intentional logout)
 */
export async function clearAllAuthStorage() {
  memoryToken = null;
  memoryUser = null;
  memoryProfile = 'guest';

  try {
    localStorage.removeItem(STORAGE_KEY_AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEY_AUTH_USER);
    localStorage.setItem(STORAGE_KEY_PROFILE, 'guest');
  } catch {}

  try {
    await Promise.all([
      Preferences.remove({ key: STORAGE_KEY_AUTH_TOKEN }),
      Preferences.remove({ key: STORAGE_KEY_AUTH_USER }),
      Preferences.set({ key: STORAGE_KEY_PROFILE, value: 'guest' })
    ]);
  } catch (err) {
    console.warn('[SecureStorage] Clear all storage warning:', err);
  }
}
